import { useState } from "react";
import { api, day, dateTime } from "../../api.js";
import { useResource } from "../useResource.js";
import { shortName } from "../common.jsx";
import { pickKey } from "../Movement.jsx";
import { KINDS } from "../../../../lib/pn/model.js";
import { totals, kopUi } from "../../../../lib/pn/calc.js";
import Money from "../../components/Money.jsx";
import PnStock from "./PnStock.jsx";

// Вибране в «Русі товарів і послуг» (#/c/<id>/pn?items=…): коди та окремі позиції (p.<ПН>.<рядок>, m.<МД>.<товар>[.<позиція>])
const ITEM = /^([0-9][0-9.]{0,19}|p\.\d{1,20}\.\d{1,5}|m\.[0-9A-Z]{1,40}\.\d{1,5}(\.\d{1,5})?)$/;
const hashParam = (k) => { const q = location.hash.split("?")[1]; return q ? new URLSearchParams(q).get(k) : null; };
const itemsFromHash = () => {
  const q = location.hash.split("?")[1];
  const v = q ? new URLSearchParams(q).get("items") : null;
  return v ? [...new Set(v.split(",").map((x) => x.trim()).filter((x) => ITEM.test(x)))].slice(0, 50) : [];
};

// «Створити накладну»: що оформлюємо, чернетки, на основі ПН (повтор виданої чи перепродаж з вхідної, з націнкою), товари з «Руху».
// Непідтримані сценарії видно з позначкою «Скоро» — не видаємо їх за готові
export default function PnNew({ companyId, company }) {
  const canEdit = ["preparer", "submitter", "owner"].includes(company.role);
  const [kind, setKind] = useState("sale");
  const items = itemsFromHash(), fromMove = items.length > 0;
  const dropCodes = () => { location.hash = `#/c/${companyId}/pn`; };
  // На основі ПН: src — "out" (повторити видану) або "in" (перепродати з вхідної), repeat — код ПН у ЄРПН, markup — %
  const fromHash = /^[0-9]{1,20}$/.test(hashParam("from") || "") ? hashParam("from") : null;
  const [src, setSrc] = useState(""), [repeat, setRepeat] = useState(""), [markup, setMarkup] = useState("");
  // «Продаж із залишків» (або прийшли з «Руху»): вибрані позиції й кількість — від PnStock, null — вибір неповний
  const [stock, setStock] = useState(false), [stockVal, setStockVal] = useState(null);
  const stockMode = fromMove || stock;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const pre = useResource(`/api/companies/${companyId}/pn/prefill${fromHash ? `?from=${fromHash}` : ""}`);
  // Прийшли зі списку ПН/РК з конкретної накладної — одразу вибираємо її
  const preFrom = pre.data?.from;
  const [fromDone, setFromDone] = useState(false); // лише раз: далі вибір — за людиною
  if (preFrom && fromHash && !fromDone) { setFromDone(true); setSrc(preFrom.direction); setRepeat(preFrom.code); }
  const [reload, setReload] = useState(0);
  const drafts = useResource(`/api/companies/${companyId}/pn/drafts`, reload);
  const discard = async (d) => {
    if (!window.confirm(`Видалити чернетку ${d.doc.number ? `ПН № ${d.doc.number}` : "без номера"}? Якщо її вже створено в кабінеті ДПС (і не надіслано), там її теж буде видалено.`)) return;
    try { await api(`/api/companies/${companyId}/pn/drafts/${d.id}/discard`, { method: "POST" }); } catch (e) { window.alert(e.message); }
    setReload((x) => x + 1);
  };

  const start = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await api(`/api/companies/${companyId}/pn/drafts`, { method: "POST", body: stockMode ? { kind, ...stockVal }
        : { kind: repeat ? "sale" : kind, ...(repeat ? { fromInvoice: repeat, ...(markupOk && markupN > 0 ? { markup: markup.trim() } : {}) } : {}) } });
      if (fromMove) try { sessionStorage.removeItem(pickKey(companyId)); } catch {}
      location.hash = `#/c/${companyId}/pn/${r.draft.id}`;
    } catch (err) { setError(err); setBusy(false); }
  };
  // Список для вибору: останні видані чи отримані ПН, плюс та, з якої прийшли (якщо її там немає)
  const withFrom = (list, dir) => (preFrom && preFrom.direction === dir && !list.some((r) => r.code === preFrom.code)
    ? [{ ...preFrom, buyer: preFrom.party, seller: preFrom.party }, ...list] : list);
  const recent = withFrom(pre.data?.recent || [], "out"), recentIn = withFrom(pre.data?.recentIn || [], "in");
  const srcList = src === "in" ? recentIn : recent;
  const markupOk = /^\s*\d{1,4}([.,]\d{1,2})?\s*$/.test(markup) || markup.trim() === "";
  const markupN = Number(markup.trim().replace(",", ".")) || 0;
  const pickSrc = (d) => { const l = d === "in" ? recentIn : recent; setSrc(d); setRepeat(l[0]?.code || ""); };

  const ready = Object.entries(KINDS).filter(([, v]) => v.ready), soon = Object.entries(KINDS).filter(([, v]) => !v.ready && !v.merged);
  const list = drafts.data?.drafts || [];

  return (
    <>
      <div className="section-head"><div><h2>Створити накладну</h2>
        <p className="status">Оберіть, що оформлюєте. Решту підставимо самі, а перед підписом покажемо ПН на офіційному бланку.</p></div>
        <a className="btn btn-secondary btn-sm" href={`#/c/${companyId}/erpn`}>До списку ПН/РК</a></div>

      {!canEdit && <p className="card warn">У вас роль «перегляд» — створювати накладні можуть ролі «підготовка», «подання» і «власник».</p>}

      {canEdit && (
        <form className="card pn-start" onSubmit={start}>
          <fieldset className="pn-choice">
            <legend className="sr-only">Що ви хочете оформити?</legend>
            {ready.map(([k, v]) => (
              <label key={k} className={`pn-option${!repeat && !stock && kind === k ? " checked" : ""}`}>
                <input type="radio" name="kind" value={k} checked={!repeat && !stock && kind === k} onChange={() => { setKind(k); setRepeat(""); setSrc(""); setStock(false); }} />
                <span><b>{v.title}</b><span className="muted small">{v.hint}</span></span>
              </label>
            ))}
            {!fromMove && (
              <label className={`pn-option${stock ? " checked" : ""}`}>
                <input type="radio" name="kind" value="stock" checked={stock} onChange={() => { setStock(true); setRepeat(""); setSrc(""); setKind("sale"); }} />
                <span><b>Продаж із залишків</b><span className="muted small">Товари, що є на складі за рухом: кількість не більша за залишок, ціна — ціна надходження з вашою націнкою.</span></span>
              </label>
            )}
            {!fromMove && recent.length > 0 && (
              <label className={`pn-option${repeat && src === "out" ? " checked" : ""}`}>
                <input type="radio" name="kind" value="repeat" checked={Boolean(repeat) && src === "out"} onChange={() => { setStock(false); pickSrc("out"); }} />
                <span><b>Повторити видану ПН</b><span className="muted small">Покупець і товари — як у вибраній ПН, ціни — з націнкою, якщо вкажете.</span></span>
              </label>
            )}
            {!fromMove && recentIn.length > 0 && (
              <label className={`pn-option${repeat && src === "in" ? " checked" : ""}`}>
                <input type="radio" name="kind" value="resale" checked={Boolean(repeat) && src === "in"} onChange={() => { setStock(false); pickSrc("in"); }} />
                <span><b>Перепродати з вхідної ПН</b><span className="muted small">Товари й ціни постачальника плюс ваша націнка. Покупця вкажете в майстрі.</span></span>
              </label>
            )}
          </fieldset>
          {stockMode && (
            <div className="pn-from-move">
              {fromMove && <p className="small"><b>З «Руху товарів і послуг»</b> <span className="muted">— вибрані коди й позиції з їхнім залишком і ціною надходження.</span></p>}
              <PnStock companyId={companyId} keys={fromMove ? items : null} onChange={setStockVal} />
              {fromMove && <div className="row"><a className="btn btn-ghost btn-sm" href={`#/c/${companyId}/movement`}>← Змінити вибір</a>
                <button type="button" className="btn btn-ghost btn-sm" onClick={dropCodes}>Без товарів з руху</button></div>}
            </div>
          )}
          {repeat && (
            <fieldset className="pn-repeat">
              <legend className="small"><b>{src === "in" ? "З якої вхідної ПН?" : "Яку накладну повторити?"}</b> <span className="muted">Останні {srcList.length} {src === "in" ? "отриманих" : "виданих"} ПН</span></legend>
              {srcList.map((r) => (
                <label key={r.code} className={`pn-repeat-item${repeat === r.code ? " checked" : ""}`}>
                  <input type="radio" name="repeat" value={r.code} checked={repeat === r.code} onChange={() => setRepeat(r.code)} />
                  <span className="pn-repeat-doc"><b>ПН № {r.nmr}</b><span className="muted small">від {day(r.crtdate)}</span></span>
                  <span className="pn-repeat-buyer" title={src === "in" ? r.seller : r.buyer}>{shortName(src === "in" ? r.seller : r.buyer) || "—"}{r.tin ? <span className="muted small"> · {r.tin}</span> : null}</span>
                  <span className="pn-repeat-sum"><Money value={r.total} /> грн</span>
                </label>
              ))}
              <div className="pn-markup">
                <label className="small" htmlFor="pn-markup"><b>Націнка до цін</b></label>
                <span className="pn-markup-field"><input id="pn-markup" className="input" inputMode="decimal" placeholder="0" value={markup} onChange={(e) => setMarkup(e.target.value)}
                  aria-invalid={!markupOk} aria-describedby="pn-markup-hint" /> %</span>
                <p id="pn-markup-hint" className={`small ${markupOk ? "muted" : "error"}`}>{!markupOk ? "Вкажіть відсоток числом, до двох знаків після коми, напр. 12,5."
                  : `Ціну без ПДВ кожного рядка збільшимо на ${markupN ? `${markup.trim()} %` : "вказаний відсоток"} і округлимо до копійки. Ціни можна змінити в майстрі.`}</p>
                {src === "in" && markupOk && !markupN && <p className="notice warn small" role="note">Продаж за ціною купівлі ДПС бачить як транзит: таких постачальників і покупців комісія називає поіменно. Без націнки майте пояснення, навіщо така операція.</p>}
              </div>
            </fieldset>
          )}
          <p className="muted small pn-soon">Скоро: {soon.map(([, v]) => v.title[0].toLowerCase() + v.title.slice(1)).join(", ")}. Поки що такі документи складайте в кабінеті ДПС.</p>
          {pre.data && !pre.data.seller && (
            <p className="notice error" role="alert">Не знайшли вашої зареєстрованої ПН, з якої беремо реквізити продавця. Синхронізуйте розділ ПН/РК і поверніться сюди.</p>
          )}
          {error && <p className="error" role="alert">{error.message}</p>}
          <div className="actions"><button type="submit" className="btn" disabled={busy || (Boolean(repeat) && !markupOk) || (stockMode && !stockVal)}>{busy ? "Створюємо чернетку…" : "Далі →"}</button>
            <span className="muted small">Чернетка зберігається лише в eCabinet — у ДПС нічого не надсилається.</span></div>
        </form>
      )}

      <details className="card pn-drafts-box">
        <summary><b>Чернетки накладних</b> <span className="muted small">{drafts.data ? list.length : "…"}</span></summary>
        {drafts.error ? <p className="error">{drafts.error.message}</p> : !drafts.data ? <p className="muted">Завантаження…</p> :
          !list.length ? <p className="muted">Чернеток немає.</p> : (
            <ul className="pn-drafts">
              {list.map((d) => {
                const t = totals(d.doc.rows || [], d.doc.priceMode);
                return (
                  <li key={d.id} className="pn-draft-item"><a href={`#/c/${companyId}/pn/${d.id}`}>
                    <b>{d.doc.number ? `ПН № ${d.doc.number}` : "ПН без номера"}{d.doc.date ? ` від ${day(d.doc.date)}` : ""}</b>
                    <span>{KINDS[d.kind]?.title} · {d.kind === "nonpayer" ? "Неплатник" : shortName(d.doc.buyer?.name) || "покупця не вказано"}</span>
                    <span className="muted small">{t.total ? `${kopUi(t.total)} грн з ПДВ · ` : ""}{d.status === "reviewed" ? "бланк переглянуто · " : ""}змінено {dateTime(d.updatedAt)}</span>
                  </a>{canEdit && <button type="button" className="btn btn-ghost btn-sm pn-del-btn" onClick={() => discard(d)} aria-label={`Видалити чернетку ${d.doc.number || ""}`}>Видалити</button>}</li>
                );
              })}
            </ul>
          )}
      </details>
    </>
  );
}
